import Polygon from 'ol/geom/Polygon';
import { getArea } from 'ol/sphere';

import { formatArea, getBaseUnit } from './measurementSystem';

/**
 * Derive human-friendly stats from a drawn area-of-interest ring (EPSG:3857).
 *
 * The area is kept in raw square metres — the unit the measurement is taken in —
 * so the display layer can pick a unit that suits its magnitude. Converting to
 * hectares here would force every caller to render a unit that can't express a
 * small sketch (see {@link formatAoiArea}).
 *
 * @param {Array<[number, number]>} ring - polygon ring coordinates
 * @returns {{ vertices: number, squareMeters: number } | null}
 */
export const getAoiStats = ring => {
	if (!ring?.length) return null;
	const [first] = ring;
	const last = ring[ring.length - 1];
	// OL polygon rings are closed (last point repeats the first); don't count it.
	const isClosed = ring.length > 1 && first[0] === last[0] && first[1] === last[1];
	const vertices = isClosed ? ring.length - 1 : ring.length;
	const squareMeters = getArea(new Polygon([ring]), { projection: 'EPSG:3857' });
	return { vertices, squareMeters };
};

/**
 * Format a drawn area for display, unit included, scaled to the value's size and
 * respecting the user's measurement system: `'400 m²'`, `'2.5 ha'`, `'1.2 km²'`.
 *
 * A drawn AOI can easily be smaller than a tenth of a hectare, which no fixed
 * hectare figure can show as anything but zero — hence the scaling.
 *
 * @param {number|null|undefined} squareMeters
 * @returns {string|null} `null` when there's nothing measurable to show
 */
export const formatAoiArea = squareMeters => {
	if (typeof squareMeters !== 'number' || !Number.isFinite(squareMeters)) return null;
	if (squareMeters < 0) return null;

	// Whole numbers read better in the smallest unit (m²/ft²), where a decimal is
	// noise; the scaled units (ha/km²/ac) need one to stay meaningful.
	const [, unit] = formatArea(squareMeters, 1);
	const decimalPlaces = unit === getBaseUnit('area') ? 0 : 1;
	const [value] = formatArea(squareMeters, decimalPlaces);

	return `${value} ${unit}`;
};
